import { useState } from "react";
import dayjs from "dayjs";
import NextLink from "next/link";
import Box from "@mui/material/Box";
import { Section } from "@atoms/Section";
import Text from "@ds/components/Text";
import Button from "@ds/components/Button";
import Container from "@mui/material/Container";
import { Grid, useMediaQuery, useTheme } from "@mui/material";
import { isEmpty } from "@helpers/common";
import color from "@definitions/mui/color";
import { ZoomIcon } from "@icons/index";
import BreadcrumbsComponent from "@atoms/Breadcrumbs/Breadcrumbs";
import {
  Icarousel,
  Idetail,
  Ilocalization,
  Iuvp,
  IsuccessStory,
  IBusinessSolution,
} from "@containers/BusinessSolution/interface";
import { ProductCard } from "@atoms/ProductCard";
import { Carousel } from "@atoms/Carousel";
import useStyles from "./styles";
import Footer from "@layouts/Footer";
import Header from "@layouts/Main/lib/Header";
import DynamicImage from "@ds/components/DynamicImage";
import { i18n, useTranslation } from "next-i18next";
import ZoomImage from "../ZoomImage";
import { IMG_WHATSAPP } from "@config/images";
import getAdminWA from "@helpers/getAdminWA";
import YoutubePlayer from "@fragments/YoutubePlayer/youtubePlayer";
import { gtmCustomEvent } from "@helpers/analytics";
import { isAllowInterest, isAllowWhatsapp } from "@config/feature";

const BusinessLevelZero: React.FC<IBusinessSolution> = (props: IBusinessSolution) => {
  const { children: data, dataNavbar } = props;
  const breadcrumbs = [{ label: data?.type, productPath: "#" }, { label: data?.categoryName }];

  const theme = useTheme();
  const isBelowXL = useMediaQuery(theme.breakpoints.down("xl"));
  const isBelowLG = useMediaQuery(theme.breakpoints.down("lg"));
  const language = i18n?.language;
  const { t } = useTranslation("common");

  const classes = useStyles({
    url: data?.categoryImage?.mediaPath,
  });

  const [zoom, setZoom] = useState({
    isOpen: false,
    src: "",
    alt: "",
    width: 0,
    height: 0,
  });

  const onCloseZoomImage = () => {
    setZoom({ ...zoom, isOpen: false });
  };

  const localization = data.localization?.find(
    (item: Ilocalization) => item.language === language,
  ) as Ilocalization;

  const onWAClick = () => {
    const timeoutFetch = setTimeout(() => {
      gtmCustomEvent(
        {
          eventAction: `Klik WA - Produk ${localization?.title}`,
          eventCategory: "Klik WA",
          eventLabel: `Klik WA - Produk ${localization?.title}`,
        },
        "LeadProduct",
      );
    }, 1000);
    window.open(getAdminWA(localization?.title), "_blank");
    return () => window.clearTimeout(timeoutFetch);
  };

  const carouselContent = (isOnlyImage: boolean, carouselData: Icarousel) => {
    const carouselLength = localization.carousel.filter(
      (item: Icarousel) => item.description,
    ).length;

    return (
      <>
        {isOnlyImage ? (
          <DynamicImage
            src={carouselData?.image.mediaPath}
            alt={carouselData?.image.mediaName}
            width={1920}
            height={1080}
            display="inline"
            position="relative"
            rounded
          />
        ) : (
          <Grid
            container
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
            paddingX={isBelowXL || carouselLength > 1 ? "5%" : "auto"}
            spacing={1}
          >
            <Grid item md={6} lg={6} xl={5}>
              <Box
                width="100%"
                display="flex"
                flexDirection="column"
                flexWrap="wrap"
                textAlign="center"
              >
                <Text variant="h1" weight="700" sx={{ paddingBottom: "24px", textAlign: "start" }}>
                  {carouselData.title}
                </Text>
                <Text weight="600" sx={{ textAlign: "justify" }}>
                  <Box dangerouslySetInnerHTML={{ __html: carouselData.description }} />
                </Text>
              </Box>
            </Grid>
            <Grid item md={5} lg={5} xl={5}>
              <DynamicImage
                src={carouselData?.image.mediaPath}
                alt={carouselData?.image.mediaName}
                maxHeight={270}
                position="relative"
                rounded
              />
            </Grid>
          </Grid>
        )}
      </>
    );
  };

  return (
    <>
      <Header />
      <Box className={classes.base}>
        {zoom.isOpen ? (
          <ZoomImage
            isOpen={zoom.isOpen}
            src={zoom.src}
            alt={zoom.alt}
            width={zoom.width}
            height={zoom.height}
            isMobile={false}
            onCloseZoomImage={onCloseZoomImage}
          />
        ) : null}
        <Section variant="gray" maxWidth="full-width" px="16px" paddingY={1}>
          <Container maxWidth="lg" disableGutters>
            <BreadcrumbsComponent
              homeUrl={{ label: "Beranda", productPath: "/" }}
              breadcrumbs={breadcrumbs}
            />
          </Container>
        </Section>
        <Section maxWidth="full-width" width="100%" px="110px">
          <Container maxWidth="lg" disableGutters>
            <Grid container direction="row" justifyContent="space-between" alignItems="center">
              <Grid item xs={7}>
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="flex-start"
                  flexWrap="wrap"
                  mr={4}
                >
                  <Text variant="h1" weight="700" sx={{ paddingBottom: "24px" }}>
                    {localization?.title}
                  </Text>
                  <Box mt={2}>
                    <Text
                      color="inactive-secondary"
                      variant="body1"
                      weight="600"
                      sx={{ textAlign: "justify" }}
                    >
                      <Box dangerouslySetInnerHTML={{ __html: localization?.story }} />
                    </Text>
                  </Box>
                  <Box display="flex" gap={2} mt={4}>
                    {isAllowInterest && (
                      <NextLink href={`/interest-form`}>

                        <Button size="large">{t("interest.button")}</Button>

                      </NextLink>
                    )}
                    {!isEmpty({}) && (
                      <NextLink href={""} target="_blank">

                        <Button variant="secondary">{t("interest.learn")}</Button>

                      </NextLink>
                    )}
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={5}>
                <Box display="flex" justifyContent="flex-end" alignItems="flex-start">
                  {data.productYoutubeId ? (
                    <YoutubePlayer
                      image={data.categoryImage}
                      productYoutubeId={data.productYoutubeId}
                    />
                  ) : (
                    <DynamicImage
                      src={data.categoryImage?.mediaPath}
                      alt={data.categoryImage?.mediaName}
                      position="relative"
                      rounded
                      priority
                    />
                  )}
                </Box>
              </Grid>
            </Grid>
          </Container>
        </Section>
        <Section bgcolor={color.grey.mid} maxWidth="full-width" px="110px" pt={4} pb={5}>
          <Container maxWidth="lg" disableGutters>
            <Box
              display="flex"
              gap={localization?.uvp?.length < 4 ? 6 : 0}
              textAlign="center"
              alignItems="flex-start"
              justifyContent={localization?.uvp?.length < 4 ? "center" : "space-between"}
              flexDirection="row"
            >
              {localization?.uvp?.map((el: Iuvp, idx: number) => (
                <Box key={idx} className={classes.containerUVP}>
                  <DynamicImage
                    src={el.uvpImage.mediaPath}
                    alt={el.uvpImage.mediaName}
                    width={120}
                    height={120}
                    position="relative"
                    rounded
                  />
                  <Text weight="600" variant="h2">
                    {el.uvpTitle}
                  </Text>
                  <Text weight="600" color="inactive-secondary">
                    {el.uvpDescription}
                  </Text>
                </Box>
              ))}
            </Box>
          </Container>
        </Section>
        {data.successStory.length ? (
          <Section
            title={t("businessSolution.helpWith") + " " + localization?.title}
            maxWidth="full-width"
            sx={{ ".slick-dots": { bottom: "-60px" } }}
          >
            <Container maxWidth="lg" disableGutters>
              <Carousel
                settings={{
                  dots: true,
                  slidesToShow: 4,
                }}
              >
                {data.successStory?.map((item: IsuccessStory, idx: number) => (
                  <ProductCard
                    title={item.title}
                    key={idx}
                    subTitle={dayjs(item.createdAt).format("DD MMM YYYY")}
                    image={item.image?.mediaPath}
                    htmlDescription={item.story}
                    linkTo={`/insight/${item.slug}`}
                    maxWidth="280px"
                    height="370px"
                    marginX={1}
                    overflow="hidden"
                  />
                ))}
              </Carousel>
            </Container>
          </Section>
        ) : null}
        {data.isActivePillar && (
          <Section paddingY="60px" maxWidth="full-width" borderTop="1px solid #DEE3ED">
            <Container maxWidth={isBelowLG ? "md" : "lg"} disableGutters>
              <Grid container justifyContent="space-between" spacing={1}>
                <Grid item md={5} lg={6} xl={5} textAlign="center">
                  <DynamicImage
                    src={localization?.pillar.image.mediaPath}
                    alt={localization?.pillar.image.mediaName}
                    position="relative"
                    rounded
                  />
                  <Box pt="24px">
                    <Button
                      variant="secondary"
                      size="small"
                      onClick={() =>
                        setZoom({
                          isOpen: true,
                          src: localization?.pillar.image.mediaPath,
                          alt: localization?.pillar.image.mediaName,
                          width: 640,
                          height: 640,
                        })
                      }
                    >
                      View Detail <ZoomIcon />
                    </Button>
                  </Box>
                </Grid>
                <Grid item justifyContent="center" md={6} lg={5} xl={5}>
                  {localization?.pillar?.titleSection && (
                    <Text
                      weight="700"
                      variant="h1"
                      sx={{ paddingBottom: "24px", textAlign: "start" }}
                    >
                      {localization?.pillar?.titleSection}
                    </Text>
                  )}
                  {localization?.pillar?.detail?.map((el: Idetail, idx: number) => (
                    <Box key={idx} pb="24px">
                      <Box display="flex" alignItems="center" gap={1} pb="16px">
                        <Box className={classes.roundNumber}>
                          <Text color="primary-main">{idx + 1}</Text>
                        </Box>
                        <Text color="primary-main">{el.title}</Text>
                      </Box>
                      <Text sx={{ fontFamily: "Nunito" }} color="inactive-secondary">
                        {el.description}
                      </Text>
                    </Box>
                  ))}
                </Grid>
              </Grid>
            </Container>
          </Section>
        )}
        {data?.isActiveCarousel && (
          <Section
            maxWidth="full-width"
            sx={{ ".slick-dots": { bottom: "-50px" } }}
            borderTop="1px solid #DEE3ED"
            pt="60px"
            pb="80px"
          >
            <Container maxWidth={isBelowLG ? "md" : "lg"} disableGutters>
              <Carousel
                arrowPosition="-1vw"
                settings={{
                  dots: true,
                  slidesToShow: 1,
                  adaptiveHeight: true,
                }}
              >
                {data.isOnlyImageCarousel
                  ? localization?.carousel.map((item: Icarousel, idx: number) => (
                      <>
                        <Box px={1} key={idx}>
                          <Box textAlign="center">
                            <Text variant="h1" weight="700" sx={{ paddingBottom: "40px" }}>
                              {item.title}
                            </Text>
                            {carouselContent(data.isOnlyImageCarousel, item)}
                          </Box>
                        </Box>
                        {localization?.carousel.length === 1 ? (
                          <Box pt="16px" textAlign="center">
                            <Button
                              variant="secondary"
                              size="small"
                              onClick={() =>
                                setZoom({
                                  isOpen: true,
                                  src: item?.image.mediaPath,
                                  alt: item?.image.mediaName,
                                  width: 1080,
                                  height: 720,
                                })
                              }
                            >
                              View Detail <ZoomIcon />
                            </Button>
                          </Box>
                        ) : null}
                      </>
                    ))
                  : localization?.carousel
                      .filter((itemFilter: Icarousel) => itemFilter.description)
                      .map((item: Icarousel, idx: number) => (
                        <Box px={1} key={idx}>
                          {carouselContent(data.isOnlyImageCarousel, item)}
                        </Box>
                      ))}
              </Carousel>
            </Container>
          </Section>
        )}
        {data?.isActiveProblemSector && (
          <Section maxWidth="full-width" borderTop="1px solid #DEE3ED" paddingY="60px">
            <Container maxWidth={isBelowLG ? "md" : "lg"} disableGutters>
              <Grid container justifyContent="space-between" spacing={1}>
                <Grid item textAlign="center" md={5} lg={6} xl={5}>
                  <DynamicImage
                    src={localization?.problemSector?.image?.mediaPath}
                    alt={localization?.problemSector?.image?.mediaName}
                    position="relative"
                    rounded
                  />
                  <Box pt="24px">
                    <Button
                      variant="secondary"
                      size="small"
                      onClick={() =>
                        setZoom({
                          isOpen: true,
                          src: localization?.problemSector.image.mediaPath,
                          alt: localization?.problemSector.image.mediaName,
                          width: 1080,
                          height: 720,
                        })
                      }
                    >
                      View Detail <ZoomIcon />
                    </Button>
                  </Box>
                </Grid>
                <Grid item justifyContent="center" md={6} lg={5} xl={5}>
                  {localization?.problemSector?.title && (
                    <Text
                      weight="700"
                      variant="h1"
                      sx={{ paddingBottom: "24px", textAlign: "start" }}
                    >
                      {localization?.problemSector?.title}
                    </Text>
                  )}
                  <Box>
                    <Text
                      sx={{ fontFamily: "Nunito", textAlign: "justify" }}
                      color="inactive-secondary"
                    >
                      <Box
                        dangerouslySetInnerHTML={{
                          __html: localization?.problemSector?.description,
                        }}
                      />
                    </Text>
                  </Box>
                </Grid>
              </Grid>
            </Container>
          </Section>
        )}
        <Section variant="gray" maxWidth="full-width">
          <Container maxWidth={isBelowLG ? "md" : "lg"} disableGutters>
            <Box
              textAlign="center"
              paddingY="60px"
              borderRadius="16px"
              className={classes.gainAccess}
            >
              <Container maxWidth="sm">
                <Text className={classes.titleReguler} variant="h2" color="white" weight="700">
                  {t("businessSolution.interest.title") +
                    " " +
                    localization?.title +
                    (language === "id" ? "" : "?")}
                </Text>
                <Text component="p" variant="body1" color="white" sx={{ marginBottom: 20 }}>
                  {t("businessSolution.interest.desc") +
                    " " +
                    (language === "en" ? "" : localization?.title)}
                </Text>
              </Container>
              <Box className={classes.interestButtonContainer}>
                {isAllowWhatsapp && (
                  <Button
                    onClick={onWAClick}
                    size="large"
                    variant="outlined"
                    width="230px"
                    className={classes.whatsAppButton}
                    startIcon={<img src={IMG_WHATSAPP} alt="whatsapp" />}
                  >
                    {t("interest.whatsapp")}
                  </Button>
                )}
                {isAllowInterest && (
                  <NextLink href="/interest-form">

                    <Button size="large" width="230px">
                      {t("businessSolution.interest.button")}
                    </Button>

                  </NextLink>
                )}
              </Box>
            </Box>
          </Container>
        </Section>
        <Footer bgColor="white" dataNavbar={dataNavbar || []} />
      </Box>
    </>
  );
};

export default BusinessLevelZero;
